import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";

const DROPDOWN_MAX_HEIGHT = 240; // matches max-h-60 (15rem = 240px)

export const CustomSelect = ({
  value,
  options,
  onChange,
  placeholder = "Select...",
  className = "",
  triggerClassName = "",
  chipStyle = false,
  compact = false,
}: {
  value: string | number | null;
  options: { label: string; value: any }[];
  onChange: (val: any) => void;
  placeholder?: string;
  className?: string;
  triggerClassName?: string;
  chipStyle?: boolean;
  compact?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [dropUp, setDropUp] = useState(false);

  const buttonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });

  const sortedOptions = useMemo(
    () =>
      [...options].sort((a, b) => {
        const aKey = a.label.includes("-")
          ? a.label.split("-")[1].trim()
          : a.label;
        const bKey = b.label.includes("-")
          ? b.label.split("-")[1].trim()
          : b.label;
        return aKey.localeCompare(bKey);
      }),
    [options],
  );

  const selectedOption = sortedOptions.find(
    (o) => String(o.value) === String(value),
  );

  const filteredOptions = sortedOptions.filter((op) =>
    op.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchTerm]);

  // Reposition on scroll/resize while open
  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const shouldDropUp =
        spaceBelow < DROPDOWN_MAX_HEIGHT && spaceAbove > spaceBelow;

      setDropUp(shouldDropUp);
      setCoords({
        top: shouldDropUp ? rect.top : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen]);

  const toggleOpen = () => {
    if (!isOpen) {
      setSearchTerm("");
      setHighlightedIndex(0);
    }
    setIsOpen(!isOpen);
  };

  const handleSelect = (val: any) => {
    onChange(val);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "Enter" || e.key === "ArrowDown") toggleOpen();
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % filteredOptions.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex(
          (prev) =>
            (prev - 1 + filteredOptions.length) % filteredOptions.length,
        );
        break;
      case "Enter":
        e.preventDefault();
        if (filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex].value);
        }
        break;
      case "Escape":
        setIsOpen(false);
        break;
      case "Tab":
        // Let the browser move focus naturally; just close the dropdown.
        if (filteredOptions[highlightedIndex] && searchTerm) {
          handleSelect(filteredOptions[highlightedIndex].value);
        } else {
          setIsOpen(false);
        }
        break;
    }
  };

  // Compute dropdown style: anchor above or below the trigger
  const dropdownStyle: React.CSSProperties = dropUp
    ? {
        bottom: window.innerHeight - coords.top + 4,
        left: coords.left,
        width: coords.width,
      }
    : { top: coords.top, left: coords.left, width: coords.width };

  return (
    <div
      ref={buttonRef}
      className={`relative shrink-0 ${className}`}
      onKeyDown={handleKeyDown}
    >
      <div
        onClick={toggleOpen}
        tabIndex={0}
        role="combobox"
        aria-expanded={isOpen}
        className={chipStyle
          ? `w-full inline-flex items-center justify-between gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-all cursor-pointer outline-none ${
              selectedOption || isOpen
                ? "bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 shadow-sm"
                : "text-gray-500 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200"
            } ${triggerClassName}`
          : `w-full flex items-center justify-between px-2.5 ${compact ? "py-1" : "py-1.5"} bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-sm transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/50 focus-within:ring-2 focus-within:ring-primary/50 ${triggerClassName}`}
      >
        {isOpen ? (
          <input
            autoFocus
            type="text"
            className={`bg-transparent border-none outline-none w-full p-0 ${chipStyle ? "text-xs" : "text-sm"}`}
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            className={`truncate ${chipStyle
              ? (selectedOption ? "text-gray-900 dark:text-slate-100" : "text-gray-500 dark:text-slate-400")
              : (!selectedOption ? "text-slate-400" : "text-slate-900 dark:text-white")
            }`}
          >
            {selectedOption?.label || placeholder}
          </span>
        )}
        <ChevronDown
          size={chipStyle ? 10 : 16}
          className={`shrink-0 transition-transform duration-200 ${chipStyle ? "text-gray-400 dark:text-slate-500" : "text-slate-900 dark:text-white"} ${isOpen ? "rotate-180" : ""}`}
        />
      </div>

      {isOpen &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[9998]"
              onClick={() => setIsOpen(false)}
            />
            <div
              ref={dropdownRef}
              className="fixed z-[9999] bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-2xl overflow-hidden flex flex-col max-h-60"
              style={dropdownStyle}
            >
              <div className="overflow-y-auto custom-scrollbar">
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((op, index) => {
                    const isHighlighted = index === highlightedIndex;
                    const isSelected = String(op.value) === String(value);

                    return (
                      <button
                        key={op.value}
                        type="button"
                        onMouseEnter={() => setHighlightedIndex(index)}
                        onClick={() => handleSelect(op.value)}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                          isHighlighted
                            ? "bg-primary text-white"
                            : isSelected
                              ? "bg-primary/10 text-primary font-bold"
                              : "text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                        }`}
                      >
                        {op.label}
                      </button>
                    );
                  })
                ) : (
                  <div className="px-3 py-4 text-center text-xs text-slate-400">
                    No results found
                  </div>
                )}
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
};
